import { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

export interface ProgressBarPropsRoot extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  children?: string | (number | string)[] | ReactNode;
  percentage?: number;
}

const getProgressBarStyle = (percentage?: number): CSSProperties => ({
  transform: percentage
    ? `translateX(-${100 - percentage}%)`
    : "translateX(-100%)",
});

const ProgressBarContainer = (props: ProgressBarPropsRoot) => {
  return (
    <div
      className={twMerge(
        "h-2.5 w-full overflow-hidden rounded-full bg-yellow-200",
        props.className,
      )}
    >
      {props.children}
    </div>
  );
};

const ProgressBarRoot = (props: ProgressBarPropsRoot) => {
  return (
    <div
      className={twMerge(
        "h-2.5 rounded-full bg-yellow-700 transition-transform",
        props.className,
      )}
      style={getProgressBarStyle(props.percentage)}
    ></div>
  );
};

const Yellow = (props: ProgressBarPropsRoot) => {
  return (
    <ProgressBarContainer
      {...props}
      className={twMerge("bg-yellow-200", props.className)}
    >
      <ProgressBarRoot
        percentage={props.percentage}
        className={"bg-yellow-500"}
      />
    </ProgressBarContainer>
  );
};

const Green = (props: ProgressBarPropsRoot) => {
  return (
    <ProgressBarContainer
      {...props}
      className={twMerge("bg-emerald-200", props.className)}
    >
      <ProgressBarRoot
        percentage={props.percentage}
        className={"bg-emerald-500"}
      />
    </ProgressBarContainer>
  );
};

const Orange = (props: ProgressBarPropsRoot) => {
  return (
    <ProgressBarContainer
      {...props}
      className={twMerge("bg-amber-200", props.className)}
    >
      <ProgressBarRoot
        percentage={props.percentage}
        className={"bg-amber-500"}
      />
    </ProgressBarContainer>
  );
};

const Violet = (props: ProgressBarPropsRoot) => {
  return (
    <ProgressBarContainer
      {...props}
      className={twMerge("bg-violet-200", props.className)}
    >
      <ProgressBarRoot
        percentage={props.percentage}
        className={"bg-violet-500"}
      />
    </ProgressBarContainer>
  );
};

const Purple = (props: ProgressBarPropsRoot) => {
  return (
    <ProgressBarContainer
      {...props}
      className={twMerge("bg-purple-200", props.className)}
    >
      <ProgressBarRoot
        percentage={props.percentage}
        className={"bg-purple-800"}
      />
    </ProgressBarContainer>
  );
};

const Blue = (props: ProgressBarPropsRoot) => {
  return (
    <ProgressBarContainer
      {...props}
      className={twMerge("bg-blue-200", props.className)}
    >
      <ProgressBarRoot
        percentage={props.percentage}
        className={"bg-blue-500"}
      />
    </ProgressBarContainer>
  );
};

const Red = (props: ProgressBarPropsRoot) => {
  return (
    <ProgressBarContainer
      {...props}
      className={twMerge("bg-red-200", props.className)}
    >
      <ProgressBarRoot percentage={props.percentage} className={"bg-red-500"} />
    </ProgressBarContainer>
  );
};

const Indigo = (props: ProgressBarPropsRoot) => {
  return (
    <ProgressBarContainer
      {...props}
      className={twMerge("bg-indigo-100", props.className)}
    >
      <ProgressBarRoot
        percentage={props.percentage}
        className={"bg-indigo-800"}
      />
    </ProgressBarContainer>
  );
};

export {
  Yellow as ProgressBar,
  Yellow as ProgressBarYellow,
  Green as ProgressBarGreen,
  Orange as ProgressBarOrange,
  Purple as ProgressBarPurple,
  Blue as ProgressBarBlue,
  Red as ProgressBarRed,
  Indigo as ProgressBarIndigo,
  Violet as ProgressBarViolet,
};
